// cookieUtils.js
// Utilidades para manejar el usuario en cookies

import { isTrustedMessageOrigin } from "../utils/messagingOrigins";

// Debug logging flag (disabled for production)
const debugLog = (...args) => {
  // Disabled for production
  // if (typeof window !== 'undefined' && window.owlyManageDebug) {
  //   console.log('[Owly Manage Debug]', ...args);
  // }
};

// SSO state management
let ssoInProgress = false;
let lastSSOAttempt = 0;
let backendFailureCount = 0;
let ssoAutoDisabled = false;
const SSO_COOLDOWN = 30000; // 30 seconds
const MAX_BACKEND_FAILURES = 3;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}

// Función para obtener usuario desde cookies (acepta variantes de nombre y formatos)
export function getUserCookie() {
  try {
    // Probar múltiples nombres por compatibilidad con LMS/campus
    const cookieNames = [
      "autenticator_user",      // nombre canónico histórico (sin h)
      "authenticator_user",     // variante común (con h) usada por otros apps
      "sso_user_data"           // fallback para datos SSO normalizados
    ];

    for (const name of cookieNames) {
      const raw = getCookie(name);
      if (!raw) continue;

      try {
        // Parser tolerante: intenta JSON estricto, URL-decoding y estilo Python
        const parsed = parsePossiblyPythonObject(raw) || JSON.parse(decodeURIComponent(raw));
        if (parsed && typeof parsed === 'object' && (parsed.email || parsed.username)) {
          debugLog(`[DEBUG] User data loaded from ${name} cookie`);

          // Si vino de un alias, normalizar guardando la cookie canónica
          if (name !== "autenticator_user") {
            try {
              setUserCookie(parsed);
              // borrar alias para evitar duplicados
              const currentDomain = window.location.hostname;
              try {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${currentDomain}; secure; SameSite=None`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
              } catch (_) {}
            } catch (_) {}
          }

          return parsed;
        }
      } catch (error) {
        console.warn(`[Owly Manage] Error parsing ${name} cookie:`, error);
        // Si la cookie existe pero es inválida, intentar limpiar solo esa variante
        try {
          const currentDomain = window.location.hostname;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${currentDomain}; secure; SameSite=None`;
        } catch (_) {}
      }
    }
  } catch (error) {
    console.warn("[Owly Manage] Error in getUserCookie:", error);
  }
  return null;
}

// Función para validar la integridad de los datos de usuario
export function validateUserData(userData) {
  if (!userData || typeof userData !== 'object') {
    return false;
  }

  // Validaciones básicas
  const hasValidIdentifier = userData.email || userData.username;
  const hasValidAuth = userData.access || userData.token;

  return hasValidIdentifier && hasValidAuth;
}

// Guardar login MANUAL -> solo autenticator_user
export function setUserCookie(user) {
  // USAR hostname completo explícitamente para evitar inferencia automática del navegador
  const currentDomain = window.location.hostname;
  console.log('[Owly Manage] 🔧 Setting cookie with explicit domain:', currentDomain);

  const payload = encodeURIComponent(JSON.stringify(user));

  // Escribir SOLO la canónica
  document.cookie = `autenticator_user=${payload}; path=/; domain=${currentDomain}; secure; SameSite=None`;

  // Información de depuración
  if (user && (user.username || user.email)) {
    console.log('[Owly Manage] ✅ User cookie set (autenticator_user)');
  }
}

/**
 * Normaliza los datos de usuario para SSO cross-domain
 * Maneja la nueva estructura del backend donde los datos vienen anidados en 'user'
 */
function normalizeUserDataForSSO(userData) {
  if (!userData) return null;

  // Si ya está en el formato correcto (estructura plana), retornarlo
  if (userData.username && userData.email && !userData.user) {
    return userData;
  }

  // Manejar nueva estructura del backend donde los datos vienen anidados
  let userInfo = userData;
  let accessToken = userData.access;
  let refreshToken = userData.refresh;

  // Si los datos del usuario están anidados en 'user'
  if (userData.user) {
    userInfo = userData.user;
    accessToken = userData.access;
    refreshToken = userData.refresh;
  }

  // Normalizar estructura de datos
  const normalized = {
    username: userInfo.username,
    email: userInfo.email,
    access: accessToken || userData.access_token,
    refresh: refreshToken || userData.refresh_token,
    is_staff: userInfo.is_staff || false,
    is_superuser: userInfo.is_superuser || false,
    is_active: userInfo.is_active !== false, // Default true
    name: userInfo.name || userInfo.username,
    date_joined: userInfo.date_joined,
    // En la nueva estructura, organization_id contiene el objeto completo
    organization_id: typeof userInfo.organization_id === 'object' ? userInfo.organization_id.id : userInfo.organization_id,
    organization: typeof userInfo.organization_id === 'object' ? userInfo.organization_id : userData.organization
  };

  // Normalizar role_id - puede venir como objeto o ID
  if (userInfo.role_id) {
    if (typeof userInfo.role_id === 'object') {
      normalized.role_id = userInfo.role_id;
    } else {
      // Si es solo un ID, construir objeto básico
      normalized.role_id = {
        id: userInfo.role_id,
        name: userInfo.role_name || "User"
      };
    }
  }

  // Limpiar campos undefined
  Object.keys(normalized).forEach(key => {
    if (normalized[key] === undefined) {
      delete normalized[key];
    }
  });

  return normalized;
}

// Robust parser that accepts JSON or Python-style dict strings (single quotes,
// True/False/None). Tries strict JSON first, then converts common Python tokens
// and single quotes into valid JSON and parses again.
function parsePossiblyPythonObject(rawStr) {
  if (!rawStr) return null;
  if (typeof rawStr === 'object') return rawStr;

  let working = String(rawStr);
  try {
    return JSON.parse(working);
  } catch (e) { }

  try {
    const decoded = decodeURIComponent(working);
    if (decoded !== working) working = decoded;
  } catch (_) { }

  working = working.replace(/\bNone\b/g, 'null');
  working = working.replace(/\bTrue\b/g, 'true');
  working = working.replace(/\bFalse\b/g, 'false');
  working = working.replace(/'([^']*)'/g, function (m, p1) {
    return JSON.stringify(p1);
  });

  try {
    return JSON.parse(working);
  } catch (e) {
    return null;
  }
}

// Función para guardar datos SSO en cookie después de extraerlos de URL
export function setSSOUserCookie(user) {
  if (!user) return null;

  // Normalizar datos antes de guardar
  const normalizedUser = normalizeUserDataForSSO(user);

  // Usar el mismo patrón que el resto del archivo
  const currentDomain = window.location.hostname;
  document.cookie = `sso_user_data=${encodeURIComponent(JSON.stringify(normalizedUser))}; path=/; domain=${currentDomain}; secure; SameSite=None`;

  return normalizedUser;
}

export function removeUserCookie() {
  const currentDomain = window.location.hostname;

  // Solo limpiar autenticator_user (SSO eliminado)
  document.cookie = `autenticator_user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${currentDomain}; secure; SameSite=None`;
  // también limpiar alias por si existe
  document.cookie = `authenticator_user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${currentDomain}; secure; SameSite=None`;
}

// Función para limpiar completamente todos los datos de autenticación
export function clearAllAuthData() {
  // Limpiar cookie del usuario
  removeUserCookie();

  // Limpiar localStorage
  try {
    localStorage.removeItem('autenticator_user');
    localStorage.removeItem('user_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('owly_user');
    localStorage.removeItem('owly_session');
    console.log('[Owly Manage] 💾 localStorage cleared');
  } catch (error) {
    console.warn('[Owly Manage] ⚠️ Error clearing localStorage:', error);
  }

  // Limpiar cualquier otra clave relacionada con autenticación
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.includes('auth') || key.includes('token') || key.includes('user') || key.includes('sso')) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Error limpiando localStorage:', error);
  }

  // Limpiar sessionStorage
  try {
    sessionStorage.removeItem('autenticator_user');
    sessionStorage.removeItem('user_token');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user_data');
    sessionStorage.removeItem('owly_session');
    console.log('[Owly Manage] 💾 sessionStorage cleared');
  } catch (error) {
    console.warn('[Owly Manage] ⚠️ Error clearing sessionStorage:', error);
  }

  // Limpiar cualquier otra clave relacionada con autenticación
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.includes('auth') || key.includes('token') || key.includes('user') || key.includes('sso')) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Error limpiando sessionStorage:', error);
  }

  // Limpiar todas las cookies relacionadas con autenticación
  const cookiesToClear = [
    'autenticator_user',
    'authenticator_user',
    'auth_token',
    'access_token',
    'refresh_token',
    'user_token',
    'edx-user-info'
  ];

  // NO usar dominio - limpiar cookies del hostname actual
  console.log('[Owly Manage] 🔧 Clearing all auth cookies without domain for hostname:', window.location.hostname);

  cookiesToClear.forEach(cookieName => {
    // Limpiar cookie sin dominio (usa hostname actual automáticamente)
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; SameSite=None`;
  });
}

/**
 * Get EdX user info from cookie (edx-user-info)
 * Checks current domain and parent domain
 */
export function getEdxUserInfo() {
  try {
    // Check current domain first
    let edxCookie = getCookieByName('edx-user-info');

    // If not found and we're in an iframe, try parent domain
    if (!edxCookie && window.parent !== window) {
      try {
        edxCookie = getCookieByName('edx-user-info', true);
      } catch (e) {
        debugLog('[DEBUG] Cannot access parent domain cookies:', e.message);
      }
    }

    if (edxCookie) {
      const parsed = JSON.parse(decodeURIComponent(edxCookie));
      debugLog('[DEBUG] EdX user info found:', parsed);
      return parsed;
    }

    debugLog('[DEBUG] No EdX user info cookie found');
    return null;
  } catch (error) {
    debugLog('[DEBUG] Error parsing EdX user info:', error);
    return null;
  }
}

/**
 * Get cookie by name with optional parent domain check
 */
function getCookieByName(name, checkParent = false) {
  const cookies = checkParent && window.parent !== window
    ? (window.parent.document ? window.parent.document.cookie : document.cookie)
    : document.cookie;

  const value = `; ${cookies}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
}

/**
 * Check if SSO attempt is allowed (cooldown and failure limits)
 */
function canAttemptSSO() {
  if (ssoAutoDisabled) {
    debugLog('[DEBUG] SSO auto-disabled due to previous errors');
    return false;
  }

  if (ssoInProgress) {
    debugLog('[DEBUG] SSO already in progress');
    return false;
  }

  const now = Date.now();
  if (now - lastSSOAttempt < SSO_COOLDOWN) {
    debugLog('[DEBUG] SSO cooldown active, waiting...');
    return false;
  }

  if (backendFailureCount >= MAX_BACKEND_FAILURES) {
    debugLog('[DEBUG] Too many backend failures, SSO disabled');
    return false;
  }

  return true;
}

/**
 * Mark backend failure for circuit breaker logic
 */
function markBackendFailure() {
  backendFailureCount++;
  debugLog('[DEBUG] Backend failure count:', backendFailureCount);
}

/**
 * Build SSO URL for iframe or redirect
 */
function buildSSOUrl(responseMode = 'redirect') {
  const isProduction = window.location.hostname.includes('owly.aulasneo.com');
  const baseUrl = isProduction
    ? process.env.REACT_APP_BASE_URL_PROD
    : process.env.REACT_APP_BASE_URL_DEV; // Usar localhost para desarrollo

  // Backend unificado espera 'origin' (p. ej. https://apps.campus.aulasneo.com)
  const params = new URLSearchParams({
    response_mode: responseMode,
    origin: window.location.origin,
  });

  // Usar endpoint correcto /by-origin/ según configuración del backend
  return `${baseUrl}/api/auth/connect/by-origin/?${params.toString()}`;
}

/**
 * Trigger SSO in iframe for silent login
 */
function triggerSSOInIframe(isManualLogin = false) {
  return new Promise((resolve, reject) => {
    debugLog('[DEBUG] Starting iframe SSO attempt...');

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '0';
    iframe.style.height = '0';

    let resolved = false;
    let timeoutId;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const resolveOnce = (result) => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(result);
      }
    };

    const rejectOnce = (error) => {
      if (!resolved) {
        resolved = true;
        cleanup();
        reject(error);
      }
    };

    // Set timeout
    timeoutId = setTimeout(() => {
      debugLog('[DEBUG] Iframe SSO timeout');
      rejectOnce(new Error('SSO iframe timeout'));
    }, 30000);

    // Error handler for iframe
    iframe.onerror = () => {
      debugLog('[DEBUG] Iframe error event triggered');
      if (isManualLogin) {
        // For manual login, redirect to main site on error
        window.location.href = 'https://www.owly.aulasneo.com/';
        return;
      }
      rejectOnce(new Error('Iframe failed to load'));
    };

    // Load handler to check for errors in iframe content
    iframe.onload = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        const iframeUrl = iframe.contentWindow.location.href;

        // Check for error 400 in URL or content
        if (iframeUrl.includes('error=400') || iframeUrl.includes('sso_error')) {
          debugLog('[DEBUG] Error 400 detected in iframe URL');
          ssoAutoDisabled = true;

          if (isManualLogin) {
            window.location.href = 'https://www.owly.aulasneo.com/';
            return;
          }

          rejectOnce(new Error('SSO Error 400'));
          return;
        }

        // Check iframe content for error indicators
        if (iframeDoc && iframeDoc.body) {
          const bodyText = iframeDoc.body.textContent || '';
          if (bodyText.includes('400') || bodyText.includes('Bad Request') || bodyText.includes('error')) {
            debugLog('[DEBUG] Error content detected in iframe');
            ssoAutoDisabled = true;

            if (isManualLogin) {
              window.location.href = 'https://www.owly.aulasneo.com/';
              return;
            }

            rejectOnce(new Error('SSO Error in content'));
            return;
          }
        }
      } catch (e) {
        // Cross-origin restrictions, continue with fragment monitoring
        debugLog('[DEBUG] Cross-origin iframe, monitoring fragment...');
      }
    };

    // Monitor for URL fragment changes
    const checkFragment = () => {
      try {
        const iframeUrl = iframe.contentWindow.location.href;
        const fragment = iframe.contentWindow.location.hash;

        if (fragment && fragment.includes('access_token')) {
          debugLog('[DEBUG] SSO success detected in iframe fragment');
          const ssoData = parseSSOFragment(fragment);
          if (ssoData && ssoData.access) {
            resolveOnce(ssoData);
            return;
          }
        }

        if (iframeUrl.includes('error=400')) {
          debugLog('[DEBUG] Error 400 detected in iframe URL during monitoring');
          ssoAutoDisabled = true;

          if (isManualLogin) {
            window.location.href = 'https://www.owly.aulasneo.com/';
            return;
          }

          rejectOnce(new Error('SSO Error 400'));
          return;
        }
      } catch (e) {
        // Cross-origin restrictions, continue monitoring
      }

      if (!resolved) {
        setTimeout(checkFragment, 500);
      }
    };

    document.body.appendChild(iframe);
    iframe.src = buildSSOUrl('redirect');

    // Start monitoring after a short delay
    setTimeout(checkFragment, 1000);
  });
}

/**
 * Parse SSO fragment from URL
 */
function parseSSOFragment(fragment) {
  try {
    const params = new URLSearchParams(fragment.substring(1));

    // Usar la nueva estructura de datos del backend (access, refresh, user object)
    const access = params.get('access');
    const refresh = params.get('refresh');
    const message = params.get('message');
    const userJson = params.get('user');
    const organizationJson = params.get('organization');

    if (access && message && userJson) {
      // Parsear objeto user desde JSON o Python-style dict
      const user = parsePossiblyPythonObject(decodeURIComponent(userJson));
      const organization = organizationJson ? parsePossiblyPythonObject(decodeURIComponent(organizationJson)) : null;

      return {
        access,
        refresh,
        message,
        user,
        organization,
        // Mantener compatibilidad con estructura anterior
        access_token: access,
        refresh_token: refresh,
        email: user.email,
        username: user.username,
        name: user.name,
        user_id: user.id,
        is_staff: user.is_staff,
        is_superuser: user.is_superuser,
        is_active: user.is_active,
        level: user.level,
        date_joined: user.date_joined,
        success: true
      };
    }

    // Fallback para estructura legacy
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const email = params.get('email');
    const username = params.get('username');
    const name = params.get('name');

    if (accessToken && email && username) {
      return {
        access: accessToken,
        refresh: refreshToken,
        access_token: accessToken,
        refresh_token: refreshToken,
        email,
        username,
        name,
        success: true
      };
    }

    return null;
  } catch (error) {
    debugLog('[DEBUG] Error parsing SSO fragment:', error);
    return null;
  }
}

/**
 * Trigger automatic SSO login
 */
export async function triggerAutoSSO(edxUserData) {
  try {
    // Check if user is already logged in with real tokens
    const existingUserData = getUserCookie();
    if (existingUserData && existingUserData.access && existingUserData.access !== 'auto-login-token') {
      debugLog('[DEBUG] User already logged in with real tokens, skipping SSO attempt');
      return false;
    }

    // Check for EdX user info
    const edxUserInfo = getEdxUserInfo();
    if (edxUserInfo) {
      debugLog('[DEBUG] OpenEdX cookies detected, proceeding with SSO to get real backend data');
    } else {
      debugLog('[DEBUG] No OpenEdX cookies found, skipping SSO attempt');
      return false;
    }

    if (!canAttemptSSO()) {
      return false;
    }

    ssoInProgress = true;
    lastSSOAttempt = Date.now();
    debugLog('[DEBUG] Starting SSO attempt...');

    const ssoData = await triggerSSOInIframe();
    if (ssoData && ssoData.access) {
      debugLog('[DEBUG] Silent SSO successful via iframe integration');

      // Set user cookie with SSO data
      setUserCookie(ssoData);

      // Dispatch success events
      window.dispatchEvent(new CustomEvent('owly-edx-sso-success', {
        detail: { user: ssoData }
      }));
      window.dispatchEvent(new CustomEvent('sso-authenticated', {
        detail: { user: ssoData }
      }));

      return true;
    } else {
      debugLog('[DEBUG] Failed to process SSO iframe data');
      markBackendFailure();
      return false;
    }
  } catch (error) {
    debugLog('[DEBUG] Iframe SSO integration failed:', error.message);
    markBackendFailure();
    return false;
  } finally {
    ssoInProgress = false;
  }
}

// Variables para monitoreo cross-domain
let crossDomainPollingInterval = null;

/**
 * Función para verificar datos SSO cross-domain
 */
function checkCrossDomainSSO() {
  try {
    // Verificar si ya tenemos usuario logueado
    const currentUser = getUserCookie();
    if (currentUser && currentUser.access && currentUser.access !== 'auto-login-token') {
      return; // Ya tenemos usuario válido
    }

    // Verificar localStorage para datos cross-domain
    const crossDomainData = localStorage.getItem('owly_cross_domain_sso');
    if (crossDomainData) {
      try {
        const ssoData = JSON.parse(crossDomainData);
        const maxAge = 24 * 60 * 60 * 1000; // 24 horas

        if (ssoData.timestamp && (Date.now() - ssoData.timestamp) < maxAge) {
          console.log('[Owly Manage] 🔍 Found cross-domain SSO data:', {
            username: ssoData.user?.username,
            email: ssoData.user?.email,
            domain: ssoData.domain,
            age: Math.round((Date.now() - ssoData.timestamp) / 1000) + ' seconds'
          });

          // Guardar event_id si vino presente
          if (ssoData.event_id) {
            try {
              localStorage.setItem('owly_sso_event_id', ssoData.event_id);
              sessionStorage.setItem('owly_sso_event_id', ssoData.event_id);
              console.log('[Owly Manage] 🆔 Stored SSO event_id from cross-domain storage:', ssoData.event_id);
            } catch (e) {
              console.log('[Owly Manage] ❌ Error storing event_id from cross-domain storage:', e);
            }
          }

          // Establecer cookie SSO y disparar evento
          setSSOUserCookie(ssoData.user);

          // Disparar evento para que App.js detecte el login
          window.dispatchEvent(new CustomEvent('owly-cross-domain-sso-detected', {
            detail: { user: ssoData.user, source: 'cross-domain-localStorage' }
          }));

          // Limpiar datos después de procesarlos
          localStorage.removeItem('owly_cross_domain_sso');

          return;
        } else {
          // Datos expirados, limpiar
          localStorage.removeItem('owly_cross_domain_sso');
        }
      } catch (parseError) {
        console.warn('[Owly Manage] Error parsing cross-domain SSO data:', parseError);
        localStorage.removeItem('owly_cross_domain_sso');
      }
    }

    // También verificar sessionStorage como backup
    const sessionData = sessionStorage.getItem('owly_cross_domain_sso');
    if (sessionData) {
      try {
        const ssoData = JSON.parse(sessionData);
        const maxAge = 24 * 60 * 60 * 1000; // 24 horas

        if (ssoData.timestamp && (Date.now() - ssoData.timestamp) < maxAge) {
          console.log('[Owly Manage] 🔍 Found cross-domain SSO data in sessionStorage');

          if (ssoData.event_id) {
            try {
              localStorage.setItem('owly_sso_event_id', ssoData.event_id);
              sessionStorage.setItem('owly_sso_event_id', ssoData.event_id);
              console.log('[Owly Manage] 🆔 Stored SSO event_id from sessionStorage:', ssoData.event_id);
            } catch (e) {}
          }

          setSSOUserCookie(ssoData.user);
          window.dispatchEvent(new CustomEvent('owly-cross-domain-sso-detected', {
            detail: { user: ssoData.user, source: 'cross-domain-sessionStorage' }
          }));

          sessionStorage.removeItem('owly_cross_domain_sso');
          return;
        } else {
          sessionStorage.removeItem('owly_cross_domain_sso');
        }
      } catch (parseError) {
        sessionStorage.removeItem('owly_cross_domain_sso');
      }
    }

  } catch (error) {
    console.warn('[Owly Manage] Error in checkCrossDomainSSO:', error);
  }
}

/**
 * Iniciar monitoreo cross-domain
 */
function startCrossDomainMonitoring() {
  if (crossDomainPollingInterval) {
    return; // Ya está activo
  }
  // Verificar inmediatamente
  checkCrossDomainSSO();

  // Verificar cada 2 segundos
  crossDomainPollingInterval = setInterval(() => {
    checkCrossDomainSSO();
  }, 2000);
}

/**
 * Detener monitoreo cross-domain
 */
// eslint-disable-next-line no-unused-vars
function stopCrossDomainMonitoring() {
  if (crossDomainPollingInterval) {
    clearInterval(crossDomainPollingInterval);
    crossDomainPollingInterval = null;
    console.log('[Owly Manage] ⏹️ Cross-domain SSO monitoring stopped');
  }
}

/**
 * Initialize SSO auto-login functionality
 */
export function initializeAutoSSO() {
  // Enable debug logging temporarily
  if (typeof window !== 'undefined') {
    window.owlyManageDebug = true;
    debugLog('[DEBUG] SSO auto-login initialization started');
  }

  // Iniciar monitoreo cross-domain
  startCrossDomainMonitoring();

  // Listen for EdX user info requests from parent/child frames
  window.addEventListener('message', (event) => {
    if (event.data?.type === 'owly-request-edx-user-info') {
      const edxUserInfo = getEdxUserInfo();
      event.source.postMessage({
        type: 'owly-edx-user-info-response',
        data: edxUserInfo
      }, event.origin);
    }

    if (event.data?.type === 'owly-edx-user-info-response' && event.data?.data) {
      debugLog('[DEBUG] Received EdX user info from parent frame:', event.data.data);
      // Trigger SSO with received data
      triggerAutoSSO(event.data.data);
    }

    // NUEVO: Escuchar eventos SSO desde owly-frontend
    if ((event.data?.type === 'sso_login' || event.data?.type === 'sso_login_cross_domain') && event.data?.source === 'owly-frontend-chat') {
      if (!isTrustedMessageOrigin(event.origin)) {
        return;
      }

      console.log('[Owly Manage] 📥 Received SSO login event from owly-frontend:', event.data);

      try {
        const userData = event.data.user;
        // Persistir event_id si viene presente para futuras consultas /check
        if (event.data.event_id) {
          try {
            localStorage.setItem('owly_sso_event_id', event.data.event_id);
            sessionStorage.setItem('owly_sso_event_id', event.data.event_id);
            console.log('[Owly Manage] 🆔 Stored SSO event_id from postMessage:', event.data.event_id);
          } catch (e) {
            console.log('[Owly Manage] ❌ Error storing event_id from postMessage:', e);
          }
        }
        if (userData && (userData.access || userData.access_token)) {
          // Establecer los datos SSO en la cookie de owly-manage-frontend
          setSSOUserCookie(userData);
          console.log('[Owly Manage] ✅ SSO user data synchronized from owly-frontend');

          // Disparar evento local para que los componentes React se actualicen
          window.dispatchEvent(new CustomEvent('owly-sso-sync-received', {
            detail: { user: userData, source: 'owly-frontend' }
          }));
        } else {
          console.warn('[Owly Manage] ⚠️ Received SSO event but user data is incomplete');
        }
      } catch (ssoSyncError) {
        console.error('[Owly Manage] ❌ Error synchronizing SSO data:', ssoSyncError);
      }
    }

    // NUEVO: Escuchar eventos de logout desde owly-frontend
    if (event.data?.type === 'client-platform-logout' && event.data?.source === 'owly-frontend-chat') {
      console.log('[Owly Manage] 📥 Received logout event from owly-frontend:', event.data);

      try {
        // Limpiar cookies de owly-manage-frontend
        clearAllAuthData();
        console.log('[Owly Manage] ✅ Auth data cleared due to logout from owly-frontend');

        // Disparar evento local para que los componentes React se actualicen
        window.dispatchEvent(new CustomEvent('owly-logout-sync-received', {
          detail: { reason: event.data.reason, source: 'owly-frontend' }
        }));
      } catch (logoutSyncError) {
        console.error('[Owly Manage] ❌ Error processing logout from owly-frontend:', logoutSyncError);
      }
    }
  });

  // NUEVO: Listener para BroadcastChannel (mismo dominio)
  if (window.BroadcastChannel) {
    try {
      const ssoChannel = new BroadcastChannel("owly-sso-channel");
      ssoChannel.onmessage = (event) => {
        console.log('[Owly Manage] 📡 Received BroadcastChannel SSO event:', event.data);

        if (event.data?.type === 'sso_login' && event.data?.source === 'owly-frontend-chat') {
          try {
            const userData = event.data.user;
            if (event.data.event_id) {
              try {
                localStorage.setItem('owly_sso_event_id', event.data.event_id);
                sessionStorage.setItem('owly_sso_event_id', event.data.event_id);
                console.log('[Owly Manage] 🆔 Stored SSO event_id from BroadcastChannel:', event.data.event_id);
              } catch (e) {}
            }
            if (userData && (userData.access || userData.access_token)) {
              setSSOUserCookie(userData);
              window.dispatchEvent(new CustomEvent('owly-sso-sync-received', {
                detail: { user: userData, source: 'owly-frontend-broadcast' }
              }));
            }
          } catch (error) {
            console.error('[Owly Manage] ❌ Error processing BroadcastChannel SSO:', error);
          }
        }
      };

    } catch (broadcastError) {
      console.log('[Owly Manage] ⚠️ BroadcastChannel not supported:', broadcastError);
    }
  }

  // NUEVO: Listener para localStorage events (cross-tab)
  window.addEventListener('storage', (event) => {
    if (event.key === 'owly-sso-event') {
      try {
        const eventData = JSON.parse(event.newValue);

        if (eventData.type === 'SSO_LOGIN_SUCCESS' && eventData.user) {
          setSSOUserCookie(eventData.user);

          window.dispatchEvent(new CustomEvent('owly-sso-sync-received', {
            detail: { user: eventData.user, source: 'owly-frontend-storage' }
          }));
        }
      } catch (storageError) {
        console.error('[Owly Manage] ❌ Error processing localStorage SSO event:', storageError);
      }
    }

    if (event.key === 'owly-logout-event') {
      try {
        const eventData = JSON.parse(event.newValue);

        if (eventData.type === 'client-platform-logout') {
          clearAllAuthData();

          window.dispatchEvent(new CustomEvent('owly-logout-sync-received', {
            detail: { reason: eventData.reason, source: 'owly-frontend-storage' }
          }));
        }
      } catch (storageError) {
        console.error('[Owly Manage] ❌ Error processing localStorage logout event:', storageError);
      }
    }
  });

  console.log('[Owly Manage] 🔧 SSO event listeners initialized successfully');

  // Check for automatic SSO on page load
  setTimeout(() => {
    const existingUser = getUserCookie();
    if (!existingUser || !existingUser.access || existingUser.access === 'auto-login-token') {
      debugLog('[DEBUG] No valid user session found, checking for EdX cookies...');
      const edxUserInfo = getEdxUserInfo();
      if (edxUserInfo) {
        debugLog('[DEBUG] EdX user info found, triggering auto SSO');
        triggerAutoSSO(edxUserInfo);
      } else {
        debugLog('[DEBUG] No EdX user info found, requesting from parent frame...');
        if (window.parent !== window) {
          window.parent.postMessage({ type: 'owly-request-edx-user-info' }, '*');
        }
      }
    }
  }, 1000);
}
